import React, { useEffect, useState } from "react";
import {
  ArrowUp, ArrowDown, Eye, EyeOff, Copy, Trash2, Plus, Save, ChevronDown,
  ChevronRight, ImagePlus, X, History
} from "lucide-react";
import { api, fmtErr } from "../../lib/api";
import { useSite } from "../../context/AppContext";
import MediaPicker from "../../components/MediaPicker";
import { toast } from "sonner";

export const SECTION_TYPES = {
  hero: "Hero (large banner with headline)",
  banner: "Page Banner (title strip)",
  image_text: "Image + Text",
  rich_text: "Text Block",
  gallery: "Photo Gallery",
  promotions: "Promotion Cards",
  cta: "Call to Action",
  faq: "Frequently Asked Questions",
  reviews: "Reviews Carousel",
  contact_info: "Contact Information",
};

const TEMPLATES = {
  hero: { heading: "Your Headline", subtitle: "", badge: "", image: { url: "", alt: "", focal: { x: 50, y: 50 } }, ctas: [] },
  banner: { heading: "Page Title", text: "", image: { url: "", alt: "", focal: { x: 50, y: 50 } } },
  image_text: { heading: "Section Heading", body: "", image: { url: "", alt: "", focal: { x: 50, y: 50 } }, imagePosition: "left", cta: { label: "", url: "", newTab: false } },
  rich_text: { heading: "Heading", body: "" },
  gallery: { heading: "Gallery", images: [] },
  promotions: { heading: "Current Promotions", source: "all" },
  cta: { heading: "Ready to Get Started?", text: "", button: { label: "", url: "", newTab: true }, secondaryButton: { label: "", url: "", newTab: true }, showFee: false },
  faq: { heading: "Frequently Asked Questions", items: [{ question: "Question?", answer: "Answer." }] },
  reviews: { heading: "Kind Words from Travelers", items: [{ name: "Traveler", trip: "Cruise", quote: "", rating: 5 }] },
  contact_info: { heading: "Get in Touch" },
};

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-amber-500 transition-colors bg-white";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

function ImageField({ value, onChange, label = "Image" }) {
  const [picker, setPicker] = useState(false);
  const img = value || { url: "", alt: "", focal: { x: 50, y: 50 } };
  return (
    <div>
      <span className="block text-xs font-semibold text-slate-600 mb-1">{label}</span>
      <div className="flex items-start gap-3">
        {img.url ? (
          <img src={img.url} alt={img.alt || ""} className="w-24 h-16 object-cover rounded-lg border border-slate-200" />
        ) : (
          <div className="w-24 h-16 rounded-lg border border-dashed border-slate-300 flex items-center justify-center text-slate-400 text-[10px]">No image</div>
        )}
        <div className="flex-1 space-y-2">
          <button type="button" onClick={() => setPicker(true)} data-testid="image-field-choose" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0B1D3A] text-amber-200 text-xs font-semibold">
            <ImagePlus className="w-3.5 h-3.5" /> {img.url ? "Replace" : "Choose"} image
          </button>
          {img.url && (
            <button type="button" onClick={() => onChange({ ...img, url: "" })} className="ml-2 text-xs text-red-600 font-medium">Remove</button>
          )}
          <input value={img.alt || ""} onChange={(e) => onChange({ ...img, alt: e.target.value })} placeholder="Alt text (describe the photo)" className={inputCls} aria-label="Image alternative text" />
        </div>
      </div>
      <MediaPicker open={picker} onClose={() => setPicker(false)} onSelect={(sel) => onChange({ ...img, ...sel })} />
    </div>
  );
}

function CtaField({ value, onChange, label = "Button" }) {
  const cta = value || { label: "", url: "", newTab: true };
  return (
    <div>
      <span className="block text-xs font-semibold text-slate-600 mb-1">{label}</span>
      <div className="grid grid-cols-2 gap-2">
        <input value={cta.label || ""} onChange={(e) => onChange({ ...cta, label: e.target.value })} placeholder="Button text" className={inputCls} aria-label={`${label} text`} />
        <input value={cta.url || ""} onChange={(e) => onChange({ ...cta, url: e.target.value })} placeholder="https:// or /page" className={inputCls} aria-label={`${label} URL`} />
      </div>
      <label className="flex items-center gap-2 mt-1.5 text-xs text-slate-600">
        <input type="checkbox" checked={!!cta.newTab} onChange={(e) => onChange({ ...cta, newTab: e.target.checked })} className="accent-amber-600" />
        Open in a new tab (recommended for external links)
      </label>
    </div>
  );
}

function CtaListField({ value = [], onChange }) {
  const update = (i, cta) => onChange(value.map((c, idx) => (idx === i ? cta : c)));
  return (
    <div className="space-y-3">
      <span className="block text-xs font-semibold text-slate-600">Buttons</span>
      {value.map((cta, i) => (
        <div key={i} className="border border-slate-200 rounded-lg p-3">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-semibold text-slate-500">Button {i + 1}</span>
            <button type="button" onClick={() => onChange(value.filter((_, idx) => idx !== i))} className="text-red-600 text-xs font-medium">Remove</button>
          </div>
          <CtaField value={cta} onChange={(c) => update(i, c)} label="" />
          <label className="block mt-2">
            <span className="block text-xs font-semibold text-slate-600 mb-1">Style</span>
            <select value={cta.style || "primary"} onChange={(e) => update(i, { ...cta, style: e.target.value })} className={inputCls}>
              <option value="primary">Gold (primary)</option>
              <option value="outline">Outline (secondary)</option>
            </select>
          </label>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...value, { label: "Click Here", url: "", newTab: false, style: "primary" }])} className="text-xs font-semibold text-amber-700">+ Add button</button>
    </div>
  );
}

function GalleryField({ value = [], onChange }) {
  const [picker, setPicker] = useState(false);
  return (
    <div>
      <span className="block text-xs font-semibold text-slate-600 mb-1">Gallery photos</span>
      <div className="grid grid-cols-3 gap-2">
        {value.map((img, i) => (
          <div key={i} className="relative group">
            <img src={img.url} alt={img.alt || ""} className="w-full h-20 object-cover rounded-lg border border-slate-200" />
            <button type="button" onClick={() => onChange(value.filter((_, idx) => idx !== i))} aria-label="Remove photo"
              className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <X className="w-3 h-3" />
            </button>
            <input value={img.alt || ""} onChange={(e) => onChange(value.map((g, idx) => (idx === i ? { ...g, alt: e.target.value } : g)))}
              placeholder="Alt text" className="mt-1 w-full rounded border border-slate-200 px-1.5 py-1 text-[11px]" aria-label={`Photo ${i + 1} alt text`} />
          </div>
        ))}
        <button type="button" onClick={() => setPicker(true)} data-testid="gallery-add-image"
          className="h-20 rounded-lg border-2 border-dashed border-amber-400/60 flex flex-col items-center justify-center text-amber-700 text-xs font-semibold hover:bg-amber-50">
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>
      <MediaPicker open={picker} onClose={() => setPicker(false)} onSelect={(sel) => onChange([...value, sel])} />
    </div>
  );
}

function ListEditor({ items, onChange, fields, addLabel, addTemplate }) {
  const update = (i, patch) => onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs font-semibold text-slate-500">Item {i + 1}</span>
            <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-red-600 text-xs font-medium">Remove</button>
          </div>
          {fields.map((f) => (
            <Field key={f.key} label={f.label}>
              {f.type === "textarea" ? (
                <textarea rows={3} value={it[f.key] || ""} onChange={(e) => update(i, { [f.key]: e.target.value })} className={inputCls} />
              ) : f.type === "number" ? (
                <input type="number" min={1} max={5} value={it[f.key] || 5} onChange={(e) => update(i, { [f.key]: Number(e.target.value) })} className={inputCls} />
              ) : (
                <input value={it[f.key] || ""} onChange={(e) => update(i, { [f.key]: e.target.value })} className={inputCls} />
              )}
            </Field>
          ))}
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, { ...addTemplate }])} className="text-xs font-semibold text-amber-700" data-testid="list-editor-add">{addLabel}</button>
    </div>
  );
}

function SectionEditor({ section, onChange }) {
  const d = section.data;
  const set = (patch) => onChange({ ...section, data: { ...d, ...patch } });

  switch (section.type) {
    case "hero":
      return (
        <div className="space-y-3">
          <Field label="Headline (use a new line for each line)"><textarea rows={2} value={d.heading || ""} onChange={(e) => set({ heading: e.target.value })} className={inputCls} /></Field>
          <Field label="Introduction text"><textarea rows={3} value={d.subtitle || ""} onChange={(e) => set({ subtitle: e.target.value })} className={inputCls} /></Field>
          <Field label="Small badge text"><input value={d.badge || ""} onChange={(e) => set({ badge: e.target.value })} className={inputCls} /></Field>
          <ImageField value={d.image} onChange={(img) => set({ image: img })} label="Hero background photo" />
          <CtaListField value={d.ctas || []} onChange={(ctas) => set({ ctas })} />
        </div>
      );
    case "banner":
      return (
        <div className="space-y-3">
          <Field label="Page heading"><input value={d.heading || ""} onChange={(e) => set({ heading: e.target.value })} className={inputCls} /></Field>
          <Field label="Subheading"><input value={d.text || ""} onChange={(e) => set({ text: e.target.value })} className={inputCls} /></Field>
          <ImageField value={d.image} onChange={(img) => set({ image: img })} label="Banner background photo" />
        </div>
      );
    case "image_text":
      return (
        <div className="space-y-3">
          <Field label="Heading"><input value={d.heading || ""} onChange={(e) => set({ heading: e.target.value })} className={inputCls} /></Field>
          <Field label="Text (blank line starts a new paragraph)"><textarea rows={5} value={d.body || ""} onChange={(e) => set({ body: e.target.value })} className={inputCls} /></Field>
          <ImageField value={d.image} onChange={(img) => set({ image: img })} />
          <Field label="Image position">
            <select value={d.imagePosition || "left"} onChange={(e) => set({ imagePosition: e.target.value })} className={inputCls}>
              <option value="left">Left of text</option>
              <option value="right">Right of text</option>
            </select>
          </Field>
          <CtaField value={d.cta} onChange={(cta) => set({ cta })} label="Button (leave text empty to hide)" />
        </div>
      );
    case "rich_text":
      return (
        <div className="space-y-3">
          <Field label="Heading"><input value={d.heading || ""} onChange={(e) => set({ heading: e.target.value })} className={inputCls} /></Field>
          <Field label="Text (blank line starts a new paragraph)"><textarea rows={6} value={d.body || ""} onChange={(e) => set({ body: e.target.value })} className={inputCls} /></Field>
        </div>
      );
    case "gallery":
      return (
        <div className="space-y-3">
          <Field label="Heading"><input value={d.heading || ""} onChange={(e) => set({ heading: e.target.value })} className={inputCls} /></Field>
          <GalleryField value={d.images || []} onChange={(images) => set({ images })} />
        </div>
      );
    case "promotions":
      return (
        <div className="space-y-3">
          <Field label="Heading"><input value={d.heading || ""} onChange={(e) => set({ heading: e.target.value })} className={inputCls} /></Field>
          <Field label="Show">
            <select value={d.source || "all"} onChange={(e) => set({ source: e.target.value })} className={inputCls}>
              <option value="featured">Featured promotions only</option>
              <option value="all">All published promotions</option>
            </select>
          </Field>
          <p className="text-xs text-slate-500">Promotions themselves are managed in the Promotions tab.</p>
        </div>
      );
    case "cta":
      return (
        <div className="space-y-3">
          <Field label="Heading"><input value={d.heading || ""} onChange={(e) => set({ heading: e.target.value })} className={inputCls} /></Field>
          <Field label="Text"><textarea rows={3} value={d.text || ""} onChange={(e) => set({ text: e.target.value })} className={inputCls} /></Field>
          <CtaField value={d.button} onChange={(button) => set({ button })} label="Main button" />
          <CtaField value={d.secondaryButton} onChange={(secondaryButton) => set({ secondaryButton })} label="Secondary button (optional)" />
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={!!d.showFee} onChange={(e) => set({ showFee: e.target.checked })} className="accent-amber-600" />
            Show consultation fee notice under the button
          </label>
        </div>
      );
    case "faq":
      return (
        <div className="space-y-3">
          <Field label="Heading"><input value={d.heading || ""} onChange={(e) => set({ heading: e.target.value })} className={inputCls} /></Field>
          <ListEditor items={d.items || []} onChange={(items) => set({ items })} addLabel="+ Add question" addTemplate={{ question: "", answer: "" }}
            fields={[{ key: "question", label: "Question" }, { key: "answer", label: "Answer", type: "textarea" }]} />
        </div>
      );
    case "reviews":
      return (
        <div className="space-y-3">
          <Field label="Heading"><input value={d.heading || ""} onChange={(e) => set({ heading: e.target.value })} className={inputCls} /></Field>
          <ListEditor items={d.items || []} onChange={(items) => set({ items })} addLabel="+ Add review" addTemplate={{ name: "", trip: "", quote: "", rating: 5 }}
            fields={[{ key: "name", label: "Reviewer name" }, { key: "trip", label: "Trip type (e.g. Caribbean Cruise)" }, { key: "quote", label: "Review", type: "textarea" }, { key: "rating", label: "Stars (1–5)", type: "number" }]} />
        </div>
      );
    case "contact_info":
      return (
        <div className="space-y-3">
          <Field label="Heading"><input value={d.heading || ""} onChange={(e) => set({ heading: e.target.value })} className={inputCls} /></Field>
          <p className="text-xs text-slate-500">The contact details shown here are edited in the Contact Information tab.</p>
        </div>
      );
    default:
      return <p className="text-sm text-slate-500">This section type has no editable fields.</p>;
  }
}

export default function AdminPages() {
  const { refreshSite } = useSite();
  const [pages, setPages] = useState([]);
  const [slug, setSlug] = useState("home");
  const [page, setPage] = useState(null);
  const [openSection, setOpenSection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [showRev, setShowRev] = useState(false);
  const [addType, setAddType] = useState("image_text");

  useEffect(() => {
    api.get("/admin/pages").then(({ data }) => setPages(data)).catch((e) => toast.error(fmtErr(e)));
  }, []);

  const load = (s) => {
    setSlug(s);
    setPage(null);
    setOpenSection(null);
    setShowRev(false);
    api.get(`/admin/pages/${s}`).then(({ data }) => setPage(data)).catch((e) => toast.error(fmtErr(e)));
  };

  useEffect(() => { load(slug); }, []); // eslint-disable-line

  const mutateSections = (fn) => setPage((p) => ({ ...p, sections: fn([...p.sections]) }));

  const move = (i, dir) => mutateSections((arr) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return arr;
  });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/pages/${slug}`, {
        title: page.title, metaTitle: page.metaTitle, metaDescription: page.metaDescription, sections: page.sections,
      });
      setPage(data);
      refreshSite();
      toast.success("Page saved — changes are live");
    } catch (e) {
      toast.error(fmtErr(e, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  const loadRevisions = async () => {
    const { data } = await api.get(`/admin/pages/${slug}/revisions`);
    setRevisions(data);
    setShowRev(true);
  };

  const restore = async (revId) => {
    if (!window.confirm("Restore this older version? Your current version will be saved to history first.")) return;
    const { data } = await api.post(`/admin/pages/${slug}/revisions/${revId}/restore`);
    setPage(data);
    setShowRev(false);
    toast.success("Version restored");
  };

  if (!page) {
    return <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-600 rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="admin-pages">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-heading text-3xl font-semibold text-[#0B1D3A]">Pages & Sections</h2>
          <p className="text-slate-500 text-sm">Edit text, photos, and sections. Remember to press Save.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadRevisions} data-testid="admin-revisions-button" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:border-amber-500">
            <History className="w-4 h-4" /> History
          </button>
          <button onClick={save} disabled={saving} data-testid="admin-save-page-button" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-[#0B1D3A] text-amber-300 text-sm font-semibold disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Page"}
          </button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap mb-6" role="tablist" aria-label="Pages">
        {pages.map((p) => (
          <button key={p.slug} role="tab" aria-selected={slug === p.slug} onClick={() => load(p.slug)} data-testid={`admin-page-tab-${p.slug}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${slug === p.slug ? "bg-[#0B1D3A] text-amber-300" : "bg-white border border-slate-200 text-slate-600 hover:border-amber-500/50"}`}>
            {p.title}
          </button>
        ))}
      </div>

      {showRev && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6" data-testid="admin-revisions-panel">
          <h3 className="font-semibold text-[#0B1D3A] mb-3">Saved versions of this page</h3>
          {revisions.length === 0 ? (
            <p className="text-sm text-slate-500">No previous versions yet. A version is saved every time you press Save.</p>
          ) : (
            <ul className="space-y-2">
              {revisions.map((r) => (
                <li key={r.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
                  <span className="text-slate-600">{new Date(r.saved_at).toLocaleString()} · by {r.saved_by}</span>
                  <button onClick={() => restore(r.id)} data-testid={`admin-revision-restore-${r.id}`} className="text-amber-700 font-semibold text-xs">Restore this version</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6 grid sm:grid-cols-3 gap-4">
        <Field label="Page name (for your reference)"><input value={page.title || ""} onChange={(e) => setPage({ ...page, title: e.target.value })} className={inputCls} data-testid="admin-page-title" /></Field>
        <Field label="Browser tab title (SEO)"><input value={page.metaTitle || ""} onChange={(e) => setPage({ ...page, metaTitle: e.target.value })} className={inputCls} /></Field>
        <Field label="Search description (SEO)"><input value={page.metaDescription || ""} onChange={(e) => setPage({ ...page, metaDescription: e.target.value })} className={inputCls} /></Field>
      </div>

      <div className="space-y-3">
        {page.sections.map((s, i) => (
          <div key={s.id} className={`bg-white border rounded-xl overflow-hidden ${s.visible ? "border-slate-200" : "border-slate-200 opacity-60"}`} data-testid={`admin-section-${i}`}>
            <div className="flex items-center gap-2 px-4 py-3">
              <button onClick={() => setOpenSection(openSection === i ? null : i)} data-testid={`admin-section-toggle-${i}`} className="flex items-center gap-2 flex-1 text-left" aria-expanded={openSection === i}>
                {openSection === i ? <ChevronDown className="w-4 h-4 text-amber-600" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                <span className="font-semibold text-sm text-[#0B1D3A]">{SECTION_TYPES[s.type] || s.type}</span>
                <span className="text-xs text-slate-400 truncate max-w-[200px]">— {s.data?.heading || s.data?.title || ""}</span>
                {!s.visible && <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Hidden</span>}
              </button>
              <div className="flex items-center gap-1">
                <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move section up" data-testid={`admin-section-up-${i}`} className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => move(i, 1)} disabled={i === page.sections.length - 1} aria-label="Move section down" data-testid={`admin-section-down-${i}`} className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                <button onClick={() => mutateSections((arr) => { arr[i] = { ...arr[i], visible: !arr[i].visible }; return arr; })} aria-label={s.visible ? "Hide section" : "Show section"} data-testid={`admin-section-visibility-${i}`} className="p-2 rounded-lg hover:bg-slate-100">
                  {s.visible ? <Eye className="w-4 h-4 text-slate-600" /> : <EyeOff className="w-4 h-4 text-slate-400" />}
                </button>
                <button onClick={() => mutateSections((arr) => { arr.splice(i + 1, 0, { ...arr[i], id: crypto.randomUUID(), data: JSON.parse(JSON.stringify(arr[i].data)) }); return arr; })} aria-label="Duplicate section" data-testid={`admin-section-duplicate-${i}`} className="p-2 rounded-lg hover:bg-slate-100"><Copy className="w-4 h-4 text-slate-600" /></button>
                <button onClick={() => { if (window.confirm("Delete this section? This cannot be undone until you restore a saved version.")) mutateSections((arr) => arr.filter((_, idx) => idx !== i)); }} aria-label="Delete section" data-testid={`admin-section-delete-${i}`} className="p-2 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-500" /></button>
              </div>
            </div>
            {openSection === i && (
              <div className="border-t border-slate-100 p-4 bg-slate-50/60">
                <SectionEditor section={s} onChange={(ns) => mutateSections((arr) => { arr[i] = ns; return arr; })} />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-3 bg-white border border-dashed border-amber-400/60 rounded-xl p-4">
        <select value={addType} onChange={(e) => setAddType(e.target.value)} className={`${inputCls} max-w-xs`} data-testid="admin-add-section-type" aria-label="New section type">
          {Object.entries(SECTION_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button
          onClick={() => {
            mutateSections((arr) => [...arr, { id: crypto.randomUUID(), type: addType, visible: true, data: JSON.parse(JSON.stringify(TEMPLATES[addType])) }]);
            setOpenSection(page.sections.length);
          }}
          data-testid="admin-add-section-button"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-500/90 text-[#0B1D3A] text-sm font-bold hover:bg-amber-400">
          <Plus className="w-4 h-4" /> Add Section
        </button>
      </div>
    </div>
  );
}
